/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Globe, Send } from 'lucide-react';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal } from '../components/Motion';
import { ASSETS, PROJECTS, pick } from '../data/content';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLang } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const { t, lang } = useLang();
    const [form, setForm] = useState({
        full_name: '',
        company: '',
        country: '',
        email: '',
        enquiry_type: '',
        message: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.enquiry_type || form.message.length < 10) {
            toast.error(t('contact.error_title'), { description: t('contact.error_desc') });
            return;
        }
        setSubmitting(true);
        try {
            await axios.post('https://formspree.io/f/myezrbvj', form, { headers: { Accept: 'application/json' } });
            toast.success(t('contact.success_title'), { description: t('contact.success_desc') });
            setForm({ full_name: '', company: '', country: '', email: '', enquiry_type: '', message: '' });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(t('contact.error_title'), {
                description: Array.isArray(detail) ? detail[0]?.msg : (detail || t('contact.error_desc')),
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow={t('contact_page.hero_eyebrow')}
                title={t('contact_page.hero_title')}
                sub={t('contact_page.hero_sub')}
                image={ASSETS.jeddahCorniche}
            />

            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Contact Details */}
                    <ScrollReveal className="lg:col-span-5">
                        <div className="bg-navy-deep text-white p-10 md:p-12 h-full relative grain-overlay">
                            <div className="eyebrow mb-5">{t('contact_page.hq_eyebrow')}</div>
                            <h2 className="editorial-h2 text-white text-2xl md:text-3xl mb-3">
                                {t('contact_page.hq_h2')}
                            </h2>
                            <p className="text-white/65 text-sm">{t('contact_page.hq_loc')}</p>
                            <div className="hairline-gold w-16 my-8" />

                            <ul className="space-y-6 text-white/85 text-sm">
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-4 h-4 text-gold" />
                                    </div>
                                    <div>
                                        <div className="eyebrow text-gold mb-1">{t('contact_page.address_label')}</div>
                                        <div className="text-sm">2827 Hassan Naib Al Haram Street<br />8989 Al Andalus District<br />Jeddah 23326, Kingdom of Saudi Arabia</div>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center flex-shrink-0">
                                        <Phone className="w-4 h-4 text-gold" />
                                    </div>
                                    <div>
                                        <div className="eyebrow text-gold mb-1">{t('contact_page.phone_label')}</div>
                                        <div>+966 12 422 4097</div>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center flex-shrink-0">
                                        <Mail className="w-4 h-4 text-gold" />
                                    </div>
                                    <div>
                                        <div className="eyebrow text-gold mb-1">{t('contact_page.email_label')}</div>
                                        <a href="mailto:info@deltagroup-intl.com" className="link-underline">info@saudideltaenergy.com / info@deltagroup-intl.com</a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center flex-shrink-0">
                                        <Globe className="w-4 h-4 text-gold" />
                                    </div>
                                    <div>
                                        <div className="eyebrow text-gold mb-1">{t('contact_page.website_label')}</div>
                                        <div>www.saudideltaenergy.com</div>
                                    </div>
                                </li>
                            </ul>

                            <div className="mt-10 border-t border-white/10 pt-8">
                                <div className="eyebrow text-gold mb-4">{t('contact_page.inquiries_label')}</div>
                                <ul className="space-y-2 text-white/65 text-xs">
                                    {PROJECTS.map((p) => (
                                        <li key={p.slug} className="flex justify-between gap-4">
                                            <span>{pick(p, 'name', lang)}</span>
                                            <span className="text-white/40 whitespace-nowrap">{pick(p, 'country', lang)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </ScrollReveal>

                    {/* Form */}
                    <ScrollReveal delay={0.1} className="lg:col-span-7">
                        <div className="bg-white border border-navy/10 p-10 md:p-12">
                            <div className="eyebrow mb-5">{t('contact_page.form_eyebrow')}</div>
                            <h2 className="editorial-h2 text-navy text-2xl md:text-3xl mb-10">
                                {t('contact_page.form_h2')}
                            </h2>

                            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field
                                        id="full_name"
                                        label={t('contact.full_name') + ' *'}
                                        value={form.full_name}
                                        onChange={(v) => update('full_name', v)}
                                    />
                                    <Field
                                        id="company"
                                        label={t('contact.company')}
                                        value={form.company}
                                        onChange={(v) => update('company', v)}
                                    />
                                    <Field
                                        id="country"
                                        label={t('contact.country')}
                                        value={form.country}
                                        onChange={(v) => update('country', v)}
                                    />
                                    <Field
                                        id="email"
                                        label={t('contact.email') + ' *'}
                                        type="email"
                                        value={form.email}
                                        onChange={(v) => update('email', v)}
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="enquiry_type" className="text-[10px] tracking-[0.18em] uppercase text-ink/60 mb-2 block">
                                        {t('contact.enquiry_type')} *
                                    </Label>
                                    <Select value={form.enquiry_type} onValueChange={(v) => update('enquiry_type', v)}>
                                        <SelectTrigger data-testid="enquiry-type-trigger" id="enquiry_type" className="rounded-none border-x-0 border-t-0 border-b border-navy/20 focus:border-gold bg-transparent h-12 px-0 text-base">
                                            <SelectValue placeholder={t('contact.placeholder_enquiry')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="investment">{t('contact.types.investment')}</SelectItem>
                                            <SelectItem value="project">{t('contact.types.project')}</SelectItem>
                                            <SelectItem value="media">{t('contact.types.media')}</SelectItem>
                                            <SelectItem value="other">{t('contact.types.other')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="message" className="text-[10px] tracking-[0.18em] uppercase text-ink/60 mb-2 block">
                                        {t('contact.message')} *
                                    </Label>
                                    <Textarea
                                        id="message"
                                        data-testid="contact-message"
                                        rows={6}
                                        value={form.message}
                                        onChange={(e) => update('message', e.target.value)}
                                        className="rounded-none border-x-0 border-t-0 border-b border-navy/20 focus:border-gold bg-transparent px-0 text-base resize-none focus-visible:ring-0"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit"
                                    className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-navy-deep text-[12px] tracking-widest uppercase font-medium hover:bg-gold-light disabled:opacity-50 transition-colors"
                                >
                                    {submitting ? t('common.sending') : t('common.send_enquiry')}
                                    <Send className="w-4 h-4" />
                                </button>
                            </form>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* Closing quote */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <ScrollReveal>
                        <div className="eyebrow text-gold mb-8">{t('contact_page.closing_eyebrow')}</div>
                        <blockquote className="font-display italic text-white text-xl md:text-3xl leading-relaxed">
                            {t('contact_page.closing_quote')}
                        </blockquote>
                        <div className="mt-10">
                            <div className="hairline-gold w-12 mx-auto mb-5" />
                            <div className="text-gold text-sm tracking-widest uppercase">Badr Bin Mohammed Al Aiban</div>
                            <div className="text-white/55 text-xs tracking-widest uppercase mt-1">Chairman &amp; CEO · Delta International Group Ltd.</div>
                        </div>
                    </ScrollReveal>
                </div>
            </section>
        </div>
    );
};

const Field = ({ id, label, value, onChange, type = 'text' }) => (
    <div>
        <Label htmlFor={id} className="text-[10px] tracking-[0.18em] uppercase text-ink/60 mb-2 block">
            {label}
        </Label>
        <Input
            id={id}
            data-testid={`field-${id}`}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-none border-x-0 border-t-0 border-b border-navy/20 focus:border-gold bg-transparent h-11 px-0 text-base focus-visible:ring-0"
        />
    </div>
);

export default Contact;
